import { useEffect, useRef } from "react";
import type { PublicPlayer } from "backend/types";
import { api } from "./api";
import { usePoll } from "./ui";
import { PlayerGame } from "./PlayerGame";
import { HostGame } from "./HostGame";

export function GameView({
  gameId,
  me,
  onLeave,
}: {
  gameId: string;
  me: PublicPlayer;
  onLeave: () => void;
}) {
  const { data, error, refresh } = usePoll(() => api.getGame(gameId), 2500, [gameId]);
  const joinedRef = useRef(false);

  const game = data?.game ?? null;
  const isMember = !!game?.players.some((p) => p.playerId === me.id);

  // Auto-join as a player the first time a non-member opens a room.
  useEffect(() => {
    if (!game || game.amHost || isMember || game.status === "ended" || joinedRef.current) return;
    joinedRef.current = true;
    api
      .joinGame(gameId)
      .then(() => refresh())
      .catch(() => {
        joinedRef.current = false;
      });
  }, [game, isMember, gameId, refresh]);

  if (error) {
    return (
      <div className="p-6">
        <p className="text-red-500">{error}</p>
        <button onClick={onLeave} className="mt-3 text-sm text-wa hover:underline">
          ← Back to rooms
        </button>
      </div>
    );
  }
  if (!game) return <div className="p-6 text-ink/40">Loading…</div>;

  if (game.amHost) return <HostGame game={game} onLeave={onLeave} />;

  if (!isMember) {
    if (game.status === "ended") {
      return (
        <div className="p-6 text-center">
          <p className="text-ink/50">This game has ended.</p>
          <button onClick={onLeave} className="mt-3 text-sm text-wa hover:underline">
            ← Back to rooms
          </button>
        </div>
      );
    }
    return <div className="p-6 text-ink/40">Joining…</div>;
  }

  return <PlayerGame game={game} me={me} onLeave={onLeave} />;
}
